import { CheckIcon, CircleIcon } from "lucide-react";
import React from "react";
import { TTranslateFunc } from "../../types/global";
import Flex from "../Flex";
import { Typography } from "../Typography";
import { TSize, TStepperDirection } from "./common";

export interface IStep {
    description?: string;
    title: string;
}

export interface IStepProps extends IStep {
    first?: boolean;
    last?: boolean;
    size: TSize;
    status: "complete" | "incomplete" | "current";
    translate: TTranslateFunc;
    direction: TStepperDirection;
    alignText?: "left" | "center";
}

const checkSizes: Record<TSize, number> = {
    sm: 14,
    md: 16,
    lg: 20
};

const circleSizes: Record<TSize, number> = {
    sm: 8,
    md: 10,
    lg: 12
};

const iconSizes: Record<TSize, number> = {
    sm: 24,
    md: 32,
    lg: 40
};

const mobileBottomPaddings: Record<TSize, number> = {
    sm: 24,
    md: 32,
    lg: 40
};

const BORDER_WIDTH = 1;

export const Step: React.FC<IStepProps> = ({
    description,
    first,
    last,
    size,
    status,
    title,
    translate,
    direction,
    alignText = "center"
}) => {
    const checkSize = checkSizes[size];
    const circleSize = circleSizes[size];

    const checkPadding = (iconSizes[size] - checkSize - BORDER_WIDTH) / 2;
    const circlePadding = (iconSizes[size] - circleSize - BORDER_WIDTH) / 2;

    return (
        <Flex basis={0} direction={direction === "row" ? "column" : "row"} justifyContent="stretch" grow={1}>
            <Flex
                alignItems="center"
                direction={direction}
                width={alignText === "left" && direction === "row" ? "100%" : undefined}
            >
                {direction === "row" && (
                    <Flex
                        column
                        height={first ? 0 : 2}
                        backgroundColor={status === "incomplete" ? "background-primary-200" : "background-primary-800"}
                        basis={alignText === "center" ? "50%" : undefined}
                    />
                )}

                <Flex
                    column
                    color={status === "incomplete" ? "primary-200" : "primary-800"}
                    backgroundColor="background-primary-50"
                    borderColor={status === "incomplete" ? "background-primary-200" : "background-primary-800"}
                    borderRadius={iconSizes[size] / 2}
                    borderStyle="solid"
                    borderWidth={BORDER_WIDTH}
                    boxShadow={status === "current" ? "0px 0px 0px 4px #F4EBFF" : undefined}
                    p={status === "complete" ? checkPadding : circlePadding}
                >
                    {status === "complete" ? (
                        <CheckIcon size={checkSize} />
                    ) : (
                        <CircleIcon fill="currentColor" size={circleSize} />
                    )}
                </Flex>
                <Flex
                    column
                    width={direction === "column" ? (last ? 0 : 2) : undefined}
                    height={direction === "row" ? (last ? 0 : 2) : undefined}
                    backgroundColor={status === "complete" ? "background-primary-800" : "background-primary-200"}
                    basis={direction === "column" ? "100%" : alignText === "left" ? "100%" : "50%"}
                />
            </Flex>

            {title && (
                <Flex
                    column
                    pb={direction === "column" ? mobileBottomPaddings[size] : undefined}
                    pt={direction === "column" ? undefined : 16}
                    pr={16}
                    pl={alignText === "center" || direction === "column" ? 16 : undefined}
                >
                    <Typography
                        color={status === "incomplete" ? "text-primary-300" : "text-primary-900"}
                        variant={size === "sm" ? "label-small-medium" : "label-regular-medium"}
                        styles={{ textAlign: direction === "column" ? "left" : alignText }}
                    >
                        {translate(title)}
                    </Typography>
                    {description && (
                        <Typography
                            color={status === "incomplete" ? "text-primary-300" : "text-primary-700"}
                            variant={size === "sm" ? "label-small-regular" : "label-regular-regular"}
                            styles={{ textAlign: direction === "column" ? "left" : alignText }}
                        >
                            {translate(description)}
                        </Typography>
                    )}
                </Flex>
            )}
        </Flex>
    );
};
